import {FC, useEffect, useMemo, useState} from 'react'
import {api, Application, ApplicationStatus, ApiError} from '../../services/api'
import {AuthedImage} from '../../components/AuthedImage'
import {Pagination} from '../../components/Pagination'
import {Spinner} from '../../components/Spinner'
import {useToast} from '../../components/Toast'
import {PageHead} from '../../components/PageHead'
import {ListToolbar} from '../../components/ListToolbar'
import {ModalPortal} from '../../components/ModalPortal'
import {useIsMobile} from '../../hooks/useIsMobile'

const ACCENT = '#039540'
const INK = '#1b1b29'
const MUTED = '#7e8299'
const PAGE_SIZE = 10

const TABS: {label: string; value: ApplicationStatus | 'ALL'}[] = [
  {label: 'Pending', value: 'PENDING'},
  {label: 'Approved', value: 'APPROVED'},
  {label: 'Rejected', value: 'REJECTED'},
  {label: 'All', value: 'ALL'},
]

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, {year: 'numeric', month: 'short', day: 'numeric'})

const STATUS_TONE: Record<ApplicationStatus, {color: string; bg: string}> = {
  PENDING: {color: '#b8770a', bg: 'rgba(255,168,0,0.15)'},
  APPROVED: {color: '#1f9d5b', bg: 'rgba(3,149,64,0.12)'},
  REJECTED: {color: '#d9214e', bg: 'rgba(217,33,78,0.1)'},
}

const StatusPill: FC<{status: ApplicationStatus}> = ({status}) => {
  const t = STATUS_TONE[status]
  return (
    <span style={{fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', borderRadius: 999, padding: '4px 10px', color: t.color, background: t.bg}}>
      {status}
    </span>
  )
}

/** Module 3 · admin review queue — list applications, view, approve/reject. */
const ApplicationsPage: FC = () => {
  const [tab, setTab] = useState<ApplicationStatus | 'ALL'>('PENDING')
  const [apps, setApps] = useState<Application[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState<Application | null>(null)
  const isMobile = useIsMobile()

  const load = async () => {
    setLoading(true)
    setError(null)
    try {
      setApps(await api.listApplications(tab === 'ALL' ? undefined : tab))
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Failed to load applications')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void load()
    setPage(1)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return apps
    return apps.filter((a) => {
      const name = `${a.applicant?.firstName ?? ''} ${a.applicant?.lastName ?? ''}`.toLowerCase()
      const email = (a.applicant?.email ?? '').toLowerCase()
      const airline = (a.airline ?? '').toLowerCase()
      const badge = (a.employeeId ?? '').toLowerCase()
      return name.includes(q) || email.includes(q) || airline.includes(q) || badge.includes(q)
    })
  }, [apps, search])

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  useEffect(() => {
    if (page > pageCount) setPage(pageCount)
  }, [page, pageCount])
  const paged = useMemo(() => filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE), [filtered, page])

  const onReviewed = (updated: Application) => {
    setSelected(null)
    // Drop from the current list if it no longer matches the active filter.
    if (tab !== 'ALL' && updated.status !== tab) {
      setApps((list) => list.filter((a) => a.id !== updated.id))
    } else {
      setApps((list) => list.map((a) => (a.id === updated.id ? updated : a)))
    }
  }

  return (
    <div>
      <PageHead icon='🪪' eyebrow='People' title='Applications' sub='Review renter verification — approve or reject.' />

      <div style={{marginBottom: 14}}>
        <ListToolbar
          search={search}
          onSearch={(v) => {
            setSearch(v)
            setPage(1)
          }}
          placeholder='Search applicants…'
        />
      </div>

      {/* Tabs — all in one row (equal width) on mobile, wrap on desktop */}
      <div style={{display: 'flex', gap: isMobile ? 6 : 8, marginBottom: 18, flexWrap: isMobile ? 'nowrap' : 'wrap'}}>
        {TABS.map((t) => (
          <button
            key={t.value}
            onClick={() => setTab(t.value)}
            style={{
              fontSize: isMobile ? 13 : 14,
              fontWeight: 600,
              padding: isMobile ? '7px 6px' : '8px 16px',
              borderRadius: 999,
              cursor: 'pointer',
              flex: isMobile ? 1 : '0 0 auto',
              textAlign: 'center',
              whiteSpace: 'nowrap',
              border: '1px solid',
              borderColor: tab === t.value ? ACCENT : '#e5e7eb',
              background: tab === t.value ? ACCENT : '#fff',
              color: tab === t.value ? '#fff' : INK,
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className='card ccp-fade-up-2' style={{borderRadius: 16, border: 'none', boxShadow: '0 6px 18px rgba(27,27,41,0.06)'}}>
        <div className='card-body p-0'>
          {loading ? (
            <div style={{padding: 40, textAlign: 'center', color: MUTED}}>Loading…</div>
          ) : error ? (
            <div style={{padding: 40, textAlign: 'center', color: '#d9214e'}}>{error}</div>
          ) : filtered.length === 0 ? (
            <div style={{padding: 40, textAlign: 'center', color: MUTED}}>
              {search.trim() ? 'No applications match your search.' : `No ${tab === 'ALL' ? '' : tab.toLowerCase()} applications.`}
            </div>
          ) : (
            <>
              {isMobile ? (
                <div>
                  {paged.map((a) => (
                    <div key={a.id} style={{borderTop: '1px solid #f1f3f7', padding: '14px 16px'}}>
                      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10}}>
                        <div style={{minWidth: 0}}>
                          <div style={{fontWeight: 600, color: INK}}>{a.applicant?.firstName} {a.applicant?.lastName}</div>
                          <div style={{color: MUTED, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>{a.applicant?.email}</div>
                        </div>
                        <StatusPill status={a.status} />
                      </div>
                      <div style={{display: 'flex', flexWrap: 'wrap', gap: '4px 16px', marginTop: 8, fontSize: 13, color: MUTED}}>
                        <span>✈️ {a.airline}</span>
                        <span>Badge: {a.employeeId}</span>
                        <span>{fmtDate(a.createdAt)}</span>
                      </div>
                      <button onClick={() => setSelected(a)} style={{...btnPrimary, width: '100%', marginTop: 12}}>Review</button>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{overflowX: 'auto'}}>
                  <table style={{width: '100%', borderCollapse: 'collapse', fontSize: 14}}>
                    <thead>
                      <tr style={{textAlign: 'left', color: MUTED, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em'}}>
                        <th style={th}>Applicant</th>
                        <th style={th}>Airline</th>
                        <th style={th}>Badge ID</th>
                        <th style={th}>Submitted</th>
                        <th style={th}>Status</th>
                        <th style={{...th, textAlign: 'right'}}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {paged.map((a) => (
                        <tr key={a.id} style={{borderTop: '1px solid #f1f3f7'}}>
                          <td style={td}>
                            <div style={{fontWeight: 600, color: INK}}>{a.applicant?.firstName} {a.applicant?.lastName}</div>
                            <div style={{color: MUTED, fontSize: 13}}>{a.applicant?.email}</div>
                          </td>
                          <td style={{...td, color: INK}}>{a.airline}</td>
                          <td style={{...td, color: MUTED}}>{a.employeeId}</td>
                          <td style={{...td, color: MUTED}}>{fmtDate(a.createdAt)}</td>
                          <td style={td}><StatusPill status={a.status} /></td>
                          <td style={{...td, textAlign: 'right'}}>
                            <button onClick={() => setSelected(a)} style={btnPrimary}>Review</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <div style={{borderTop: '1px solid #f1f3f7'}}>
                <Pagination page={page} total={filtered.length} pageSize={PAGE_SIZE} onChange={setPage} />
              </div>
            </>
          )}
        </div>
      </div>

      {selected && <ModalPortal><ReviewModal app={selected} onClose={() => setSelected(null)} onReviewed={onReviewed} /></ModalPortal>}
    </div>
  )
}

const ReviewModal: FC<{app: Application; onClose: () => void; onReviewed: (a: Application) => void}> = ({app, onClose, onReviewed}) => {
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState<'approve' | 'reject' | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const pending = app.status === 'PENDING'
  const toast = useToast()

  const act = async (kind: 'approve' | 'reject') => {
    setBusy(kind)
    setErr(null)
    try {
      const updated = kind === 'approve' ? await api.approveApplication(app.id) : await api.rejectApplication(app.id, reason.trim() || undefined)
      const name = `${app.applicant?.firstName ?? ''} ${app.applicant?.lastName ?? ''}`.trim()
      toast.success(kind === 'approve' ? `${name || 'Applicant'} approved.` : `${name || 'Application'} rejected.`)
      onReviewed(updated)
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Action failed')
      setBusy(null)
    }
  }

  return (
    <div onClick={onClose} style={{position: 'fixed', inset: 0, background: 'rgba(8,10,14,0.5)', zIndex: 1100, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 20px', overflowY: 'auto'}}>
      <div onClick={(e) => e.stopPropagation()} style={{background: '#fff', borderRadius: 16, padding: '28px 28px', width: '100%', maxWidth: 620, boxShadow: '0 20px 60px rgba(0,0,0,0.3)'}}>
        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6}}>
          <h2 style={{fontSize: 21, fontWeight: 800, color: INK, margin: 0}}>{app.applicant?.firstName} {app.applicant?.lastName}</h2>
          <StatusPill status={app.status} />
        </div>
        <p style={{color: MUTED, fontSize: 14, margin: '0 0 20px'}}>{app.applicant?.email}</p>

        {err && <div style={{background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', borderRadius: 8, padding: '10px 14px', fontSize: 14, marginBottom: 16}}>{err}</div>}

        <div className='row g-3' style={{marginBottom: 8}}>
          <Detail label='Phone' value={app.applicant?.phone || '—'} />
          <Detail label='Airline' value={app.airline} />
          <Detail label='Employee / badge ID' value={app.employeeId} />
          <Detail label='Emergency contact' value={app.emergencyContactName} />
          <Detail label='Emergency phone' value={app.emergencyContactPhone} />
        </div>

        <div style={{display: 'flex', gap: 14, margin: '18px 0 6px', flexWrap: 'wrap'}}>
          <div style={{flex: '1 1 240px', minWidth: 0}}>
            <div style={imgLabel}>Airline badge</div>
            {app.badgeKey ? (
              <AuthedImage fileKey={app.badgeKey} alt='Badge' zoomable style={{width: '100%', height: 180, objectFit: 'cover', borderRadius: 10, border: '1px solid #eef0f4'}} />
            ) : (
              <div style={{width: '100%', height: 180, borderRadius: 10, border: '1px solid #eef0f4', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#aab0bf', fontSize: 13, background: '#f8f9fc'}}>No badge uploaded</div>
            )}
          </div>
          {app.idKey && (
            <div style={{flex: '1 1 240px', minWidth: 0}}>
              <div style={imgLabel}>Government ID</div>
              <AuthedImage fileKey={app.idKey} alt='ID' zoomable style={{width: '100%', height: 180, objectFit: 'cover', borderRadius: 10, border: '1px solid #eef0f4'}} />
            </div>
          )}
        </div>

        {app.status === 'REJECTED' && app.rejectionReason && (
          <p style={{color: '#d9214e', fontSize: 14, marginTop: 12}}>Rejected: {app.rejectionReason}</p>
        )}

        {pending && (
          <div style={{marginTop: 22, borderTop: '1px solid #f1f3f7', paddingTop: 20}}>
            <label style={{display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6}}>Rejection reason (optional)</label>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder='Shown to the applicant if you reject…' style={{width: '100%', minHeight: 70, padding: '10px 12px', border: '1px solid #e5e7eb', borderRadius: 9, fontSize: 14, boxSizing: 'border-box', resize: 'vertical', outline: 'none'}} />
            <div style={{display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16}}>
              <button onClick={onClose} style={btnGhost}>Cancel</button>
              <button onClick={() => act('reject')} disabled={!!busy} style={{...btnDanger, minWidth: 70}}>{busy === 'reject' ? <Spinner size={13} /> : 'Reject'}</button>
              <button onClick={() => act('approve')} disabled={!!busy} style={{...btnPrimary, minWidth: 78}}>{busy === 'approve' ? <Spinner size={13} /> : 'Approve'}</button>
            </div>
          </div>
        )}
        {!pending && (
          <div style={{display: 'flex', justifyContent: 'flex-end', marginTop: 20}}>
            <button onClick={onClose} style={btnGhost}>Close</button>
          </div>
        )}
      </div>
    </div>
  )
}

const Detail: FC<{label: string; value: string}> = ({label, value}) => (
  <div className='col-6'>
    <div style={{fontSize: 12, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600}}>{label}</div>
    <div style={{fontSize: 15, color: INK, fontWeight: 600, marginTop: 2}}>{value}</div>
  </div>
)

const imgLabel: React.CSSProperties = {fontSize: 12, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600, marginBottom: 6}
const th: React.CSSProperties = {padding: '14px 18px', fontWeight: 600}
const td: React.CSSProperties = {padding: '14px 18px', verticalAlign: 'middle'}
const btnBase: React.CSSProperties = {fontSize: 13, fontWeight: 600, borderRadius: 8, padding: '8px 16px', cursor: 'pointer', border: '1px solid transparent', marginLeft: 8}
const btnGhost: React.CSSProperties = {...btnBase, marginLeft: 0, background: '#fff', border: '1px solid #e5e7eb', color: INK}
const btnPrimary: React.CSSProperties = {...btnBase, background: ACCENT, color: '#fff'}
const btnDanger: React.CSSProperties = {...btnBase, background: 'rgba(217,33,78,0.1)', color: '#d9214e'}

export {ApplicationsPage}
